import React, { useState, useEffect, useMemo } from 'react';
import { Situation, CharacteristicType, Domain } from '../../types';
// import reasonableAccommodations from '../../data/user/ReasonableAccommodation.json';
import AccommodationDisplay from './AccommodationDisplay';
import { useIsMobile } from '../../hooks/useIsMobile';
import { TAG_MAP, getTagName } from '../../constants/tagMap';
import DifficultyGraphView from '../graph/DifficultyGraphView';
// @ts-ignore
import { loadStore, buildViewModel, buildFilteredViewModel } from '../../data/newDataLoader';
import { ViewModel } from '../../types/newDataStructure';
import { logSearch, logSelection } from '../../lib/analytics';
import { Domain as NewDomain } from '../../types/newDataStructure';
import StepFooter from '../layout/StepFooter';

// 8カテゴリ定義
const CATEGORIES = [
  '身体症状・体調',
  '感覚・環境',
  '注意・集中',
  '実行・計画・記憶',
  '感情・ストレス反応',
  'コミュニケーション',
  '生活・変化対応',
  '職場・社会不安'
];

// カテゴリアイコン（ステップ⑤と統一）
const CATEGORY_ICONS = {
  '身体症状・体調': '🏥',
  '感覚・環境': '💡',
  '注意・集中': '🎯',
  '実行・計画・記憶': '📋',
  '感情・ストレス反応': '❤️',
  'コミュニケーション': '💬',
  '生活・変化対応': '🔄',
  '職場・社会不安': '🏢'
};

// カテゴリの表示名マッピング
const CATEGORY_DISPLAY_NAMES = {
  '身体症状・体調': '🏥 身体・体調',
  '感覚・環境': '💡 感覚・環境',
  '注意・集中': '🎯 注意・集中',
  '実行・計画・記憶': '📋 実行・計画',
  '感情・ストレス反応': '❤️ 感情',
  'コミュニケーション': '💬 コミュ',
  '生活・変化対応': '🔄 生活・変化',
  '職場・社会不安': '🏢 職場・社会'
};

const SUGGESTS = [
  {
    id: '1',
    title: 'タスクが多すぎて整理できない',
    desc: '"緊急・重要"マトリクスで整理するシートや支援する',
    icon: '📝',
    category: '実行機能'
  },
  {
    id: '2',
    title: '注意されると強いストレス',
    desc: '注意や指導の前後で、本人の状況に応じた十分なサポートとフォローを実施する',
    icon: '😨',
    category: '対人関係'
  },
  {
    id: '3',
    title: '午後に強い眠気が来て作業効率が大きく下がる',
    desc: '眠気時間帯に打ち合わせを避け、15〜20分のパワーナップを推奨する',
    icon: '😪',
    category: '体調管理'
  },
  {
    id: '4',
    title: 'リモートワーク時の孤独感や不安',
    desc: '定期的なオンラインの1on1の実施により、個別のコミュニケーション機会を確保する',
    icon: '🧑‍💻',
    category: '対人関係'
  },
  {
    id: '5',
    title: '曖昧な指示に混乱する',
    desc: '具体的な数値や期限を明確に示し、理解度を確認する機会を設ける',
    icon: '❓',
    category: 'コミュニケーション'
  },
  {
    id: '6',
    title: '作業の切り替えが苦手',
    desc: '作業間の決まった切り替え手順を作成し、視覚的なリマインダーを活用する',
    icon: '🔄',
    category: '実行機能'
  }
];

type DifficultyItem = {
  id: string;
  title: string;
  icon: string;
  cares: any[];
};

type SituationItem = {
  domainId: string;
  situationId: string;
};

type DifficultyThinkingProps = {
  characteristics: CharacteristicType[];
  domain: Domain;
  situations: Situation[];
  onComplete: (difficulties: DifficultyItem[]) => void;
  selectedDifficulties: any[];
  onBack: () => void;
  onViewModelChange?: (viewModel: ViewModel | null | undefined) => void;
};

const DifficultyThinking: React.FC<DifficultyThinkingProps> = ({
  characteristics,
  domain,
  situations,
  onComplete,
  selectedDifficulties,
  onBack,
  onViewModelChange
}) => {
  const [input, setInput] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [viewModel, setViewModel] = useState<ViewModel | null | undefined>(null);
  const [customDifficulties, setCustomDifficulties] = useState<string[]>([]);
  const [showAccommodationDisplay, setShowAccommodationDisplay] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('身体症状・体調'); // デフォルトカテゴリ
  const [isCountAnimating, setIsCountAnimating] = useState(false); // カウントアニメーション用
  const [isMaxReached, setIsMaxReached] = useState(false); // 最大選択数達成時のアニメーション用
  const [isCountDecreasing, setIsCountDecreasing] = useState(false); // カウント減少時のアニメーション用
  const [bigNumber, setBigNumber] = useState<number | null>(null); // 大きな数字表示用
  const [isBigNumberAnimating, setIsBigNumberAnimating] = useState(false); // 大きな数字のアニメーション用
  const [isHidingCurrent, setIsHidingCurrent] = useState(false); // 現在の表示を一時的に隠す用
  const [isDecreasingNumber, setIsDecreasingNumber] = useState<number | null>(null); // 減少時の数字表示用
  const [isDecreasingAnimating, setIsDecreasingAnimating] = useState(false); // 減少時のアニメーション用
  const [showSelectionModal, setShowSelectionModal] = useState(false); // 選択済み困りごとモーダル表示用
  const [viewMode, setViewMode] = useState<'keyword' | 'category' | 'graph'>('category'); // ビューモード（キーワード or カテゴリ or グラフ）
  const [deselectedCard, setDeselectedCard] = useState<string | null>(null); // 選択解除されたカード
  const [isDeckAnimating, setIsDeckAnimating] = useState(false); // カードの束のアニメーション
  const [isDeckAdding, setIsDeckAdding] = useState(false); // カードの束に追加するアニメーション
  const [addingCard, setAddingCard] = useState<{id: string, title: string, category: string} | null>(null); // 追加されるカード
  const [removingCard, setRemovingCard] = useState<{id: string, title: string, category: string} | null>(null); // 削除されるカード
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [visibleCounts, setVisibleCounts] = useState<{[key: string]: number}>({});
  const [customSearchTerm, setCustomSearchTerm] = useState<string>(''); // カスタム検索用
  const [showCustomSearch, setShowCustomSearch] = useState<boolean>(false); // カスタム検索表示用
  const [allConcerns, setAllConcerns] = useState<any[]>([]); // 全困りごと（検索用）
  
  // 数値に応じた色を決定する関数
  const getBigNumberColor = (num: number) => {
    return 'text-yellow-500'; // 全て黄色系で統一
  };

  // カテゴリのアイコンを取得する関数
  const getCategoryIcon = (category: string) => {
    const CATEGORY_ICONS = {
      '身体症状・体調': '🏥',
      '感覚・環境': '💡',
      '注意・集中': '🎯',
      '実行・計画・記憶': '📋',
      '感情・ストレス反応': '❤️',
      'コミュニケーション': '💬',
      '生活・変化対応': '🔄',
      '職場・社会不安': '🏢'
    };
    return CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS] || '🎯';
  };

  // アコーディオンを開閉する関数
  const toggleAccordion = (cardTitle: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardTitle)) {
        newSet.delete(cardTitle);
      } else {
        newSet.add(cardTitle);
      }
      return newSet;
    });
  };

  // さらに表示する関数
  const showMore = (category: string, totalCount: number) => {
    setVisibleCounts(prev => {
      // 残り全部を表示するように変更
      const newCount = totalCount;
      return {
        ...prev,
        [category]: newCount
      };
    });
  };

  // 表示件数を取得する関数
  const getVisibleCount = (category: string, totalCount: number) => {
    const count = visibleCounts[category] || Math.min(4, totalCount);
    return count;
  };
  
  const isMobile = useIsMobile();

  // ③ページから戻ってきた際に選択状態を復元
  // ただし、①から②に画面遷移する際は選択状態をクリア
  useEffect(() => {
    // ①から②への遷移時は全ての状態を完全にクリア
    if (!selectedDifficulties || selectedDifficulties.length === 0) {
      setSelected([]);
      setCustomDifficulties([]);
      setInput('');
      setSelectedCategory('身体症状・体調');
      setShowAccommodationDisplay(false);
    } else {
      // ③ページから戻ってきた場合は選択状態を復元
      const selectedTitles = selectedDifficulties.map(d => d.title);
      setSelected(selectedTitles);
    }
  }, [selectedDifficulties]);



  // コンポーネントのアンマウント時にクリーンアップ
  useEffect(() => {
    return () => {
      // メモリリークを防ぐためのクリーンアップ
      setSelected([]);
      setCustomDifficulties([]);
      setInput('');
      setShowAccommodationDisplay(false);
    };
  }, []);

  // --- フィルタロジック ---
  // 選択特性名リスト（nameとidの両方を含める）
  const selectedCharacteristicNames = characteristics.flatMap(c => {
    const names = [c.name];
    // LDの場合、'学習障害（LD）'と'LD'の両方を含める
    if (c.id === 'LD') {
      names.push('LD');
    }
    // 他の特性もidとnameが異なる場合は追加
    if (c.id !== c.name) {
      names.push(c.id);
    }
    return names;
  });
  // 選択ドメイン名
  const selectedDomainName = domain?.name;
  // 選択場面名リスト
  const selectedSituationNames = situations.map(s => s.name);

  // 全困りごとを読み込む（検索用）
  useEffect(() => {
    const loadAllConcerns = async () => {
      try {
        const store = await loadStore();
        // 全concernsを配列に変換
        const allConcernsArray = Object.values(store.concerns).map((concern: any) => ({
          'id': concern.id,
          '困りごと内容': concern.title,
          'カテゴリ': concern.category,
          '主要タグ': concern.primary_tags.join(','),
          '補助タグ': concern.secondary_tags.join(','),
          '特性タイプ': concern.trait_types.join(','),
          'ドメイン': Object.keys(concern.contexts).join(','),
          '企業でのシチュエーション': concern.contexts['企業']?.join(',') || '',
          '教育機関でのシチュエーション': concern.contexts['教育機関']?.join(',') || '',
          '支援機関でのシチュエーション': concern.contexts['支援機関']?.join(',') || '',
          '企業具体例': concern.examples['企業']?.join(',') || '',
          '教育機関具体例': concern.examples['教育機関']?.join(',') || '',
          '支援機関具体例': concern.examples['支援機関']?.join(',') || '',
        }));
        setAllConcerns(allConcernsArray);
      } catch (error) {
        console.error('全困りごとの読み込みに失敗:', error);
      }
    };
    loadAllConcerns();
  }, []);

  // 新データ構造を読み込む（指定された流れを使用）
  useEffect(() => {
    const loadNewData = async () => {
      try {
        const query = {
          traits: selectedCharacteristicNames,
          domain: selectedDomainName || '企業',
          situations: selectedSituationNames
        };
        
        const vm = await buildFilteredViewModel(query);
        setViewModel(vm);
        onViewModelChange?.(vm);
        // console.log('新データ構造の読み込み成功（フィルタ済み）:', vm);
      } catch (error) {
        console.error('新データ構造の読み込みに失敗:', error);
        setViewModel(null);
      }
    };
    loadNewData();
  }, [characteristics, domain, situations]); // 依存配列を修正

  // フィルタ済み困りごと（新データ構造対応）
  const filteredDifficulties = useMemo(() => {
    if (!viewModel) return [];
    
    // buildFilteredViewModelで既にフィルタリング済みなので、そのまま使用
    return viewModel.map((vm: any) => ({
      'id': vm.concern.id, // conc_1～conc_123形式のIDを追加
      '困りごと内容': vm.concern.title,
      'カテゴリ': vm.concern.category,
      '主要タグ': vm.concern.primary_tags.join(','),
      '補助タグ': vm.concern.secondary_tags.join(','),
      '特性タイプ': vm.concern.trait_types.join(','),
      'ドメイン': Object.keys(vm.concern.contexts).join(','),
      '企業でのシチュエーション': vm.concern.contexts['企業']?.join(',') || '',
      '教育機関でのシチュエーション': vm.concern.contexts['教育機関']?.join(',') || '',
      '支援機関でのシチュエーション': vm.concern.contexts['支援機関']?.join(',') || '',
      '企業具体例': vm.concern.examples['企業']?.join(',') || '',
      '教育機関具体例': vm.concern.examples['教育機関']?.join(',') || '',
      '支援機関具体例': vm.concern.examples['支援機関']?.join(',') || '',
    }));
  }, [viewModel]);

  // 困りごと内容で重複排除
  const uniqueDifficulties = useMemo(() => {
  const uniqueDifficultiesMap = new Map<string, any>();
  filteredDifficulties.forEach((item: any) => {
    if (!uniqueDifficultiesMap.has(item['困りごと内容'])) {
      uniqueDifficultiesMap.set(item['困りごと内容'], item);
    }
  });
    return Array.from(uniqueDifficultiesMap.values());
  }, [filteredDifficulties]);

  // 選択状態の管理 - selectedDifficultiesの変更を監視
  useEffect(() => {
    if (selectedDifficulties && selectedDifficulties.length > 0) {
      // ③から戻ってきた場合は選択状態を復元
      const selectedTitles = selectedDifficulties.map(d => d.title);
      setSelected(selectedTitles);
    } else {
      // ①から②への遷移時は選択状態をクリア
      setSelected([]);
    }
  }, [selectedDifficulties]);

  // カスタム検索結果（全困りごとから検索）
  const customSearchResults = useMemo(() => {
    if (!customSearchTerm.trim()) return [];
    
    const searchTerm = customSearchTerm.toLowerCase();
    // 全困りごとから検索（フィルタリング前の全データ）
    const searchTarget = allConcerns.length > 0 ? allConcerns : uniqueDifficulties;
    const results = searchTarget.filter(item => {
      const title = item['困りごと内容']?.toLowerCase() || '';
      const description = item['困りごと説明']?.toLowerCase() || '';
      const mainTags = item['主要タグ']?.toLowerCase() || '';
      const subTags = item['補助タグ']?.toLowerCase() || '';
      const category = item['カテゴリ']?.toLowerCase() || '';
      const examples = [
        item['企業具体例']?.toLowerCase() || '',
        item['教育機関具体例']?.toLowerCase() || '',
        item['支援機関具体例']?.toLowerCase() || ''
      ].join(' ');
      
      return title.includes(searchTerm) || 
             description.includes(searchTerm) || 
             mainTags.includes(searchTerm) || 
             subTags.includes(searchTerm) ||
             category.includes(searchTerm) ||
             examples.includes(searchTerm);
    });
    
    
    return results;
  }, [allConcerns, uniqueDifficulties, customSearchTerm]);

  // カテゴリ別に困りごとをグループ化（検索結果も含める）
  const difficultiesByCategory = useMemo(() => {
    // uniqueDifficultiesと検索結果をマージ
    const allItemsMap = new Map<string, any>();
    
    // uniqueDifficultiesを追加
    uniqueDifficulties.forEach(item => {
      allItemsMap.set(item['困りごと内容'], item);
    });
    
    // 検索結果を追加（検索で見つかったがフィルタリング条件に合わないものも含める）
    customSearchResults.forEach(item => {
      if (!allItemsMap.has(item['困りごと内容'])) {
        allItemsMap.set(item['困りごと内容'], item);
      }
    });
    
    // allConcernsから選択した特性に関連する困りごとを追加（カテゴリ表示用）
    // Step 1で選択した特性に関連する困りごとをallConcernsから取得
    if (allConcerns.length > 0 && selectedCharacteristicNames.length > 0) {
      let addedCount = 0;
      allConcerns.forEach(item => {
        const itemTraits = item['特性タイプ']?.split(',') || [];
        // 選択した特性のいずれかが含まれている場合
        const hasSelectedTrait = selectedCharacteristicNames.some(trait => 
          itemTraits.some((itemTrait: string) => itemTrait.trim() === trait.trim())
        );
        if (hasSelectedTrait && !allItemsMap.has(item['困りごと内容'])) {
          allItemsMap.set(item['困りごと内容'], item);
          addedCount++;
        }
      });
    }
    
    // カテゴリ別にグループ化
    const result = Array.from(allItemsMap.values()).reduce((acc, item) => {
      const jsonCategory = item['カテゴリ'] || 'その他';
      
      // JSONのカテゴリ名と表示用カテゴリ名のマッピング
      const categoryMapping: { [key: string]: string } = {
        '生活・変化対応': '生活・変化対応'
      };
      
      const displayCategory = categoryMapping[jsonCategory] || jsonCategory;
      
      if (!acc[displayCategory]) {
        acc[displayCategory] = [];
      }
      acc[displayCategory].push(item);
      return acc;
    }, {} as { [key: string]: any[] });
    
    return result;
  }, [uniqueDifficulties, customSearchResults, allConcerns, selectedCharacteristicNames]);

  // 選択されたカテゴリの困りごと
  // 選択されたカテゴリに困りごとがない場合は、最初の有効なカテゴリを選択
  const availableCategories = useMemo(() => {
    return Object.keys(difficultiesByCategory).filter(category => 
      difficultiesByCategory[category] && difficultiesByCategory[category].length > 0
    );
  }, [difficultiesByCategory]);
  
  // 選択されたカテゴリが有効でない場合は、最初の有効なカテゴリに変更
  useEffect(() => {
    if (availableCategories.length > 0 && !availableCategories.includes(selectedCategory)) {
      setSelectedCategory(availableCategories[0]);
    }
  }, [availableCategories, selectedCategory]);
  
  const currentCategoryDifficulties = useMemo(() => {
    return difficultiesByCategory[selectedCategory] || [];
  }, [difficultiesByCategory, selectedCategory]);

  // 検索ワードを太文字にする関数（XSS対策済み）
  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm.trim()) return text;
    
    // XSS対策：特殊文字をエスケープ
    const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    try {
      const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
      return text.split(regex).map((part, index) => 
        regex.test(part) ? <strong key={index} className="font-bold text-teal-600">{part}</strong> : part
      );
    } catch (error) {
      // 正規表現エラーの場合は元のテキストをそのまま返す
      console.warn('Regex error in highlightSearchTerm:', error);
      return text;
    }
  };

  // --- 選択・追加ロジックは従来通り ---
  const maxSelectable = Infinity; // 制限なし
  const handleSelect = (content: string, event?: React.MouseEvent) => {
    setSelected(prev => {
      if (prev.includes(content)) {
        // 選択解除時のカードアニメーション
        setDeselectedCard(content);
        setTimeout(() => {
          setDeselectedCard(null);
        }, 300);
        
        // カードの束のアニメーション
        if (viewMode !== 'graph') {
          const cardId = `${content}-${Date.now()}`;
          // カテゴリ情報を取得
          const difficultyItem = uniqueDifficulties.find(item => item['困りごと内容'] === content);
          const category = difficultyItem ? difficultyItem['カテゴリ'] : 'その他';
          
          setRemovingCard({ id: cardId, title: content, category });
          setIsDeckAnimating(true);
          
          setTimeout(() => {
            setRemovingCard(null);
            setIsDeckAnimating(false);
          }, 600);
        }
        
        // 選択を解除（減らす）場合、アニメーションをトリガー
        setIsCountDecreasing(true);
        
        // 減少時のアニメーション処理
        setIsDecreasingNumber(prev.length - 1);
        setIsHidingCurrent(true);
        
        // 少し待ってから大きな数字を表示
        setTimeout(() => {
          setIsDecreasingAnimating(true);
          // アニメーション完了後に要素を削除し、現在の表示を復活
          setTimeout(() => {
            setIsDecreasingNumber(null);
            setIsDecreasingAnimating(false);
            setIsHidingCurrent(false);
          }, 800);
        }, 100);
        
        // 減少アニメーション終了後に状態をリセット
        setTimeout(() => setIsCountDecreasing(false), 600);
        
        // 困りごと選択解除ログ（IDのみ）
        const difficultyItem = uniqueDifficulties.find(item => item['困りごと内容'] === content);
        // IDプロパティを探す
        const difficultyId = difficultyItem?.id || difficultyItem?.ID || difficultyItem?.conc_id || content;
        
        logSelection('step2', 'difficulty_select', {
          action: 'deselect',
          difficulty_id: difficultyId, // conc_1～conc_123形式
          selected_count: prev.length - 1
        });
        
        return prev.filter(s => s !== content);
      } else if (prev.length < maxSelectable) {
        // カードの束に追加するアニメーション
        if (viewMode !== 'graph') {
          const cardId = `${content}-${Date.now()}`;
          // カテゴリ情報を取得
          const difficultyItem = uniqueDifficulties.find(item => item['困りごと内容'] === content);
          const category = difficultyItem ? difficultyItem['カテゴリ'] : 'その他';
          
          setAddingCard({ id: cardId, title: content, category });
          setIsDeckAdding(true);
          
          setTimeout(() => {
            setAddingCard(null);
            setIsDeckAdding(false);
          }, 800);
        }
        
        // 選択数が増える場合、アニメーションをトリガー
        setIsCountAnimating(true);
        
        // 現在の表示を一時的に隠す
        setIsHidingCurrent(true);
        
        // 少し待ってから大きな数字を表示
        setTimeout(() => {
          setBigNumber(prev.length + 1);
          // さらに少し待ってからアニメーション開始
          setTimeout(() => {
            setIsBigNumberAnimating(true);
            // アニメーション完了後に要素を削除し、現在の表示を復活
            setTimeout(() => {
              setBigNumber(null);
              setIsBigNumberAnimating(false);
              setIsHidingCurrent(false);
            }, 800);
          }, 200);
        }, 100);
        
        // 10/10になる瞬間を検知
        if (prev.length === 9) {
          setIsMaxReached(true);
          // 最大達成アニメーション終了後に状態をリセット
          setTimeout(() => setIsMaxReached(false), 800);
        }
        
        // アニメーション終了後に状態をリセット
        setTimeout(() => setIsCountAnimating(false), 600);
        
        // 困りごと選択ログ（IDのみ）
        const difficultyItem = uniqueDifficulties.find(item => item['困りごと内容'] === content);
        // IDプロパティを探す
        const difficultyId = difficultyItem?.id || difficultyItem?.ID || difficultyItem?.conc_id || content;
        
        logSelection('step2', 'difficulty_select', {
          action: 'select',
          difficulty_id: difficultyId, // conc_1～conc_123形式
          selected_count: prev.length + 1
        });
        
        return [...prev, content];
      } else {
        return prev;
      }
    });
  };

  const handleAddCustom = () => {
    if (input.trim() && !customDifficulties.includes(input.trim())) {
      setCustomDifficulties(prev => [...prev, input.trim()]);
      setInput('');
    }
  };

  const handleRemoveCustom = (difficulty: string) => {
    setCustomDifficulties(prev => prev.filter(d => d !== difficulty));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleAddCustom();
    }
  };

  const handleNext = () => {
    const selectedDifficulties = uniqueDifficulties
      .filter(d => selected.includes(d['困りごと内容']))
      .map((d, index) => ({
        id: String(index + 1),
        title: d['困りごと内容'],
        icon: d['アイコン'] || '📝',
        cares: [],
        accommodations: [
          {
            name: '配慮案A',
            description: d['配慮内容'],
            examples: {
              workplace: d['企業の具体的配慮例'],
              education: d['教育機関の具体的配慮例'],
              support: d['支援機関の具体的配慮例']
            }
          }
        ].filter(acc => acc.description)
      }));

    const customDifficultiesWithIds = customDifficulties.map((d, index) => ({
      id: `custom-${index + 1}`,
      title: d,
      category: 'その他',
      icon: '📝',
      cares: [],
      accommodations: []
    }));

    // 選択された困りごとをオブジェクト形式に変換
    const selectedDifficultiesWithDetails = selectedDifficulties.map(difficulty => {
      const difficultyItem = uniqueDifficulties.find(item => item['困りごと内容'] === difficulty.title);
      const finalId = difficultyItem ? difficultyItem.id : difficulty.id;
      
      return {
        id: finalId, // conc_1等のIDを使用
        title: difficulty.title,
        category: difficultyItem ? difficultyItem['カテゴリ'] : 'その他',
        icon: difficulty.icon,
        cares: difficulty.cares,
        accommodations: difficulty.accommodations
      };
    });
    
    onComplete([...selectedDifficultiesWithDetails, ...customDifficultiesWithIds]);
  };

  // SUGGESTSの代わりにselectedDifficultiesを使う
  const difficultiesToShow = selectedDifficulties && selectedDifficulties.length > 0 ? selectedDifficulties : SUGGESTS;

  const handleRestart = () => {
    setShowAccommodationDisplay(false);
    setSelected([]);
    setCustomDifficulties([]);
  };

  const handleBack = () => {
    setShowAccommodationDisplay(false);
    setSelectedDomain(null);
  };

  // カテゴリタブのレンダリング
  const renderCategoryTabs = () => {
    if (isMobile) {
  return (
        <div className="grid grid-cols-4 gap-2 mb-6">
          {CATEGORIES.map(category => {
            const count = difficultiesByCategory[category]?.length || 0;
            return (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                disabled={count === 0}
                className={`px-2 py-2 text-xs rounded-lg border transition ${
                  selectedCategory === category
                    ? 'bg-teal-500 text-white border-teal-500'
                    : count === 0
                      ? 'bg-sand text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-teal-50'
                }`}
              >
                <div className="text-center">
                  <div className="text-sm mb-1">{CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS]}</div>
                  <div className="font-medium">{CATEGORY_DISPLAY_NAMES[category as keyof typeof CATEGORY_DISPLAY_NAMES].split(' ')[1]}</div>
                </div>
              </button>
            );
          })}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-4 gap-3 mb-6">
        {CATEGORIES.map(category => {
          const count = difficultiesByCategory[category]?.length || 0;
          return (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              disabled={count === 0}
              className={`px-3 py-3 text-sm rounded-lg border transition ${
                selectedCategory === category
                  ? 'bg-teal-500 text-white border-teal-500'
                  : count === 0
                    ? 'bg-sand text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-teal-50'
              }`}
            >
              <div className="text-center">
                <div className="text-lg mb-1">{CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS]}</div>
                <div className="font-medium text-sm">{CATEGORY_DISPLAY_NAMES[category as keyof typeof CATEGORY_DISPLAY_NAMES].split(' ')[1]}</div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };


  // モバイル用UI
  if (isMobile) {
    return (
      <div className="max-w-none mx-auto p-4 relative">
        {/* CSSアニメーション */}
        <style dangerouslySetInnerHTML={{
          __html: `
            @keyframes flyToDeck {
              0% {
                transform: translate(0, 0) scale(1) rotate(0deg);
                opacity: 1;
              }
              25% {
                transform: translate(calc(100vw - 300px), calc(100vh - 400px)) scale(0.9) rotate(45deg);
                opacity: 0.9;
              }
              50% {
                transform: translate(calc(100vw - 200px), calc(100vh - 250px)) scale(0.7) rotate(90deg);
                opacity: 0.8;
              }
              75% {
                transform: translate(calc(100vw - 100px), calc(100vh - 120px)) scale(0.4) rotate(180deg);
                opacity: 0.6;
              }
              100% {
                transform: translate(calc(100vw - 60px), calc(100vh - 60px)) scale(0.1) rotate(270deg);
                opacity: 0;
              }
            }
            @keyframes deselectBounce {
              0% {
                transform: scale(1) rotate(0deg);
                background: linear-gradient(to bottom right, rgb(239 246 255), rgb(219 234 254));
                border-color: rgb(129 140 248);
              }
              25% {
                transform: scale(0.95) rotate(-2deg);
                background: linear-gradient(to bottom right, rgb(254 242 242), rgb(252 231 243));
                border-color: rgb(239 68 68);
              }
              50% {
                transform: scale(1.05) rotate(2deg);
                background: linear-gradient(to bottom right, rgb(254 242 242), rgb(252 231 243));
                border-color: rgb(239 68 68);
              }
              75% {
                transform: scale(0.98) rotate(-1deg);
                background: linear-gradient(to bottom right, rgb(254 242 242), rgb(252 231 243));
                border-color: rgb(239 68 68);
              }
              100% {
                transform: scale(1) rotate(0deg);
                background: linear-gradient(to bottom right, rgb(255 255 255), rgb(255 255 255));
                border-color: rgb(209 213 219);
              }
            }
            @keyframes deckRemove {
              0% {
                transform: scale(1) translateY(0px);
                opacity: 1;
              }
              20% {
                transform: scale(1.1) translateY(-5px);
                opacity: 0.9;
              }
              40% {
                transform: scale(0.9) translateY(2px);
                opacity: 0.8;
              }
              60% {
                transform: scale(1.05) translateY(-2px);
                opacity: 0.9;
              }
              80% {
                transform: scale(0.95) translateY(1px);
                opacity: 0.95;
              }
              100% {
                transform: scale(1) translateY(0px);
                opacity: 1;
              }
            }
            @keyframes deckAdd {
              0% {
                transform: scale(1) translateY(0px);
                opacity: 1;
              }
              25% {
                transform: scale(1.2) translateY(-10px);
                opacity: 0.8;
              }
              50% {
                transform: scale(0.8) translateY(5px);
                opacity: 0.9;
              }
              75% {
                transform: scale(1.1) translateY(-3px);
                opacity: 0.95;
              }
              100% {
                transform: scale(1) translateY(0px);
                opacity: 1;
              }
            }
            @keyframes cardSlideIn {
              0% {
                transform: translate(-100px, -50px) rotate(-15deg) scale(0.8);
                opacity: 0;
              }
              50% {
                transform: translate(-20px, -20px) rotate(-5deg) scale(0.9);
                opacity: 0.8;
              }
              100% {
                transform: translate(0, 0) rotate(0deg) scale(1);
                opacity: 0;
              }
            }
            @keyframes cardSlideOut {
              0% {
                transform: translate(0, 0) rotate(0deg) scale(1);
                opacity: 0.8;
              }
              50% {
                transform: translate(-30px, -30px) rotate(-10deg) scale(0.9);
                opacity: 0.6;
              }
              100% {
                transform: translate(-100px, -50px) rotate(-20deg) scale(0.7);
                opacity: 0;
              }
            }
          `
        }} />
        {/* 選択件数固定表示 - カードの束（リスト表示時のみ） */}
        {viewMode !== 'graph' && (
          <div className="fixed bottom-20 right-0 z-40">
            <div 
              className="relative w-[120px] h-[120px] cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setShowSelectionModal(true)}
            >
              {/* カードの束の背景 */}
              <div 
                className="absolute bottom-2 right-2 w-16 h-20 bg-gradient-to-br from-cyan-600 to-cyan-800 rounded-lg shadow-lg transform rotate-3"
                style={isDeckAnimating ? { animation: 'deckRemove 0.4s ease-in-out' } : isDeckAdding ? { animation: 'deckAdd 0.5s ease-in-out' } : {}}
              ></div>
              <div 
                className="absolute bottom-3 right-3 w-16 h-20 bg-gradient-to-br from-cyan-500 to-cyan-700 rounded-lg shadow-md transform rotate-1"
                style={isDeckAnimating ? { animation: 'deckRemove 0.4s ease-in-out 0.05s' } : isDeckAdding ? { animation: 'deckAdd 0.5s ease-in-out 0.05s' } : {}}
              ></div>
              <div 
                className="absolute bottom-4 right-4 w-16 h-20 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-lg shadow-sm transform -rotate-1"
                style={isDeckAnimating ? { animation: 'deckRemove 0.4s ease-in-out 0.1s' } : isDeckAdding ? { animation: 'deckAdd 0.5s ease-in-out 0.1s' } : {}}
              ></div>
              <div 
                className="absolute bottom-5 right-5 w-16 h-20 bg-gradient-to-br from-cyan-300 to-cyan-500 rounded-lg transform -rotate-2"
                style={isDeckAnimating ? { animation: 'deckRemove 0.4s ease-in-out 0.15s' } : isDeckAdding ? { animation: 'deckAdd 0.5s ease-in-out 0.15s' } : {}}
              ></div>
              
              {/* 追加されるカードのアニメーション */}
              {addingCard && (
                <div
                  className="absolute bottom-2 right-2 w-16 h-20 bg-gradient-to-br from-cyan-100 to-cyan-200 border-2 border-cyan-400 rounded-lg shadow-lg z-10"
                  style={{ animation: 'cardSlideIn 0.8s ease-in-out forwards' }}
                >
                  <div className="p-2 h-full flex flex-col justify-center items-center">
                    <div className="text-lg mb-1">{getCategoryIcon(addingCard.category)}</div>
                    <div className="text-xs text-gray-800 text-center leading-tight">
                      {addingCard.title.length > 8 ? addingCard.title.substring(0, 8) + '...' : addingCard.title}
                    </div>
                  </div>
                </div>
              )}
              
              {/* 削除されるカードのアニメーション */}
              {removingCard && (
                <div
                  className="absolute bottom-2 right-2 w-16 h-20 bg-gradient-to-br from-red-200 to-red-400 border-2 border-red-500 rounded-lg shadow-lg z-10"
                  style={{ animation: 'cardSlideOut 0.6s ease-in-out forwards' }}
                >
                  <div className="p-2 h-full flex flex-col justify-center items-center">
                    <div className="text-lg mb-1 text-white">{getCategoryIcon(removingCard.category)}</div>
                    <div className="text-xs text-white text-center leading-tight font-semibold">
                      {removingCard.title.length > 8 ? removingCard.title.substring(0, 8) + '...' : removingCard.title}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 3/10テキスト表示 - カードの束の上に表示（リスト表示時のみ） */}
        {viewMode !== 'graph' && (
          <div className="fixed bottom-28 right-6 z-40">
          <div 
            className="text-lg font-bold cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setShowSelectionModal(true)}
          >
            {/* 数字部分 */}
            <span className={`transition-all duration-800 ease-out ${
              isHidingCurrent && bigNumber
                ? `opacity-100 text-5xl ${getBigNumberColor(bigNumber)} drop-shadow-2xl`
                : isHidingCurrent && isDecreasingNumber
                  ? 'opacity-100 text-5xl text-teal-300 drop-shadow-2xl'
                : isHidingCurrent && !bigNumber && !isDecreasingNumber
                  ? 'opacity-0'
                  : isMaxReached
                    ? 'scale-150 text-red-500 drop-shadow-2xl animate-pulse'
                    : isCountAnimating 
                      ? 'scale-125 text-yellow-300 drop-shadow-lg' 
                      : isCountDecreasing
                        ? 'scale-75 text-teal-300 drop-shadow-sm animate-bounce'
                        : bigNumber && !isBigNumberAnimating
                          ? `text-5xl ${getBigNumberColor(bigNumber)} drop-shadow-2xl`
                          : bigNumber && isBigNumberAnimating
                            ? `text-lg ${getBigNumberColor(bigNumber)}`
                                                            : isDecreasingNumber && !isDecreasingAnimating
                                  ? 'text-5xl text-teal-300 drop-shadow-2xl'
                                  : isDecreasingNumber && isDecreasingAnimating
                                    ? 'text-lg text-teal-300'
                                    : 'text-white scale-100'
            }`}>
              {isHidingCurrent && bigNumber ? bigNumber : isHidingCurrent && isDecreasingNumber ? isDecreasingNumber : selected.length}
            </span>
            {/* /10部分 - 常に表示、大きな数字表示中は色を統一 */}
            <span className={`transition-all duration-300 ${
              bigNumber ? getBigNumberColor(bigNumber).replace('text-red-500', 'text-red-300').replace('text-orange-500', 'text-orange-300').replace('text-yellow-500', 'text-yellow-300') : isDecreasingNumber ? 'text-teal-300' : 'text-white'
            }`}>枚</span>
          </div>
        </div>
        )}
        

        {/* 選択済み困りごとモーダル */}
        {showSelectionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden">
              {/* モーダルヘッダー */}
              <div className="bg-teal-500 text-white px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  📚 集めた困りごとカード
                  <span className={selected.length >= 11 ? 'text-red-500' : 'text-white'}>
                    （{selected.length}枚）
                  </span>
                </h3>
                <button 
                  onClick={() => setShowSelectionModal(false)}
                  className="text-white bg-teal-600 hover:bg-teal-700 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full"
                >
                  ✕
                </button>
              </div>
              
              {/* モーダルコンテンツ */}
              <div className="p-6 max-h-[60vh] overflow-y-auto">
                {selected.length === 0 ? (
                  <p className="text-gray-500 text-center">選択された困りごとはありません</p>
                ) : (
                  <div className="space-y-3">
                    {/* 全て削除ボタン */}
                    <div className="mb-4">
                      <button
                        onClick={() => {
                          // 全ての選択を解除
                          selected.forEach(item => handleSelect(item));
                          // モーダルを閉じる
                          setTimeout(() => setShowSelectionModal(false), 100);
                        }}
                        className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                      >
                        全て削除 ({selected.length}件)
                      </button>
                    </div>
                    
                    {/* 個別削除リスト */}
                    <div className="space-y-3">
                      {selected.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-sand rounded-lg">
                          <span className="text-gray-700 flex-1">{item}</span>
                          <button
                            onClick={() => {
                              handleSelect(item); // 選択解除（モーダルは閉じない）
                            }}
                            className="ml-3 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                          >
                            削除
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* モーダルフッター */}
              <div className="px-6 py-4 border-t bg-sand">
                <button
                  onClick={() => setShowSelectionModal(false)}
                  className="w-full px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        )}
        
        
        {/* 説明文 */}
        <div className="bg-light-sand border border-teal-500 rounded-lg p-4 mb-6">
          <p className="text-gray-700 text-base leading-relaxed">
            <span className="font-semibold">🗺️ 探索：あなたの困りごとを探そう</span><br />
            カテゴリまたはキーワードで、あなたの抱える困りごとカードを見つけましょう。
          </p>
        </div>
        
        {/* ビューモード切り替えタブ */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className={`flex ${isMobile ? 'space-x-1' : 'space-x-4'} bg-sand ${isMobile ? 'p-1' : 'p-3'} rounded-lg mb-4`}>
            <button
              onClick={() => setViewMode('category')}
              className={`${isMobile ? 'flex-1' : ''} py-2 ${isMobile ? 'px-4' : 'px-12'} rounded-md text-sm font-medium transition-colors ${
                viewMode === 'category'
                  ? 'bg-white text-teal-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              📂 カテゴリ
            </button>
            <button
              onClick={() => setViewMode('keyword')}
              className={`${isMobile ? 'flex-1' : ''} py-2 ${isMobile ? 'px-4' : 'px-12'} rounded-md text-sm font-medium transition-colors ${
                viewMode === 'keyword'
                  ? 'bg-white text-teal-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              🔍 キーワード
            </button>
          </div>
          
          {/* グラフ表示ボタン */}
          <div className="flex justify-center">
            <div className={`w-full p-1 rounded-lg ${viewMode === 'graph' ? 'bg-sand' : ''}`}>
              <button
                onClick={() => setViewMode('graph')}
                className={`w-full py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  viewMode === 'graph'
                    ? 'bg-white text-teal-600 shadow-sm'
                    : 'bg-sand text-gray-500 hover:text-gray-700'
                }`}
              >
                🔗 グラフ表示
              </button>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          {viewMode === 'keyword' ? (
            <>
              {/* キーワード検索セクション */}
              <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
                <h3 className="text-base font-medium text-gray-700 mb-4">
                  🔍 困りごとをキーワードで探す
                </h3>
                <div className="flex gap-1.5 mb-4">
                  <input
                    type="text"
                    value={customSearchTerm}
                    onChange={(e) => setCustomSearchTerm(e.target.value)}
                    placeholder="例：タスク、遅刻、休憩etc…"
                    className="flex-1 min-w-0 px-2 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <button
                    onClick={() => {
                      setShowCustomSearch(!showCustomSearch);
                      // キーワード検索ログ
                      if (!showCustomSearch && customSearchTerm.trim()) {
                        logSearch(customSearchTerm, customSearchResults.length);
                      }
                    }}
                    className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                      showCustomSearch
                        ? 'bg-teal-500 text-white shadow-lg'
                        : 'bg-teal-500 text-white hover:bg-teal-600'
                    }`}
                  >
                    検索
                  </button>
                </div>
                
                {/* 検索結果表示 */}
                {showCustomSearch && customSearchTerm.trim() && (
                  <div className="space-y-3">
                    <div className="text-sm text-gray-600 mb-3">
                      「{customSearchTerm}」の検索結果: {customSearchResults.length}件
                    </div>
                    {customSearchResults.length === 0 ? (
                      <div className="text-gray-400 text-center py-4">
                        該当する困りごとは見つかりませんでした。
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-3">
                        {customSearchResults.map((item: any) => {
              const isSelected = selected.includes(item['困りごと内容']);
              const isDisabled = !isSelected && selected.length >= maxSelectable;
                          
              return (
                            <div
                  key={item['困りごと内容']}
                              className={`rounded-lg border-2 transition-all duration-300 ${
                                isSelected
                                  ? 'border-teal-400 bg-gradient-to-br from-teal-50 to-teal-100 shadow-lg'
                                  : 'border-gray-200 bg-gradient-to-br from-gray-50 to-white hover:border-teal-300 hover:bg-gradient-to-br hover:from-teal-50 hover:to-white'
                              } ${isDisabled ? 'opacity-40' : ''}`}
                            >
                              <div className="p-3">
                                <div className="flex items-center justify-between">
                                  <button
                                    onClick={(e) => handleSelect(item['困りごと内容'], e)}
                  disabled={isDisabled}
                                    className={`flex-1 text-left transition ${
                                      isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-gray-900">
                                        {highlightSearchTerm(item['困りごと内容'], customSearchTerm)}
                                      </span>
                                      {isSelected && (
                                        <div className="ml-auto">
                                          <div className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                                            <span className="text-white text-xs">✓</span>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </button>
                                  <button
                                    onClick={() => toggleAccordion(item['困りごと内容'])}
                                    className="ml-2 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md transition-colors"
                                  >
                                    {expandedCards.has(item['困りごと内容']) ? '▲閉じる' : '▼詳細'}
                                  </button>
                                </div>
                              </div>
                              
                              {/* アコーディオン内容 */}
                              {expandedCards.has(item['困りごと内容']) && (
                                <div className="px-4 pb-4 border-t border-gray-100 pt-3 bg-gradient-to-b from-transparent to-gray-50/30">
                                  <div className="space-y-3">
                                    {/* 困りごと説明 */}
                                    {item['困りごと説明'] && (
                                      <div>
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">📝 説明</h4>
                                        <p className="text-sm text-gray-500 leading-relaxed">
                                          {item['困りごと説明']}
                                        </p>
                                      </div>
                                    )}
                                    
                                    {/* 具体例 */}
                                    {(() => {
                                      let example = '';
                                      if (domain?.name === '企業' && item['企業具体例']) {
                                        example = item['企業具体例'];
                                      } else if (domain?.name === '教育機関' && item['教育機関具体例']) {
                                        example = item['教育機関具体例'];
                                      } else if (domain?.name === '支援機関' && item['支援機関具体例']) {
                                        example = item['支援機関具体例'];
                                      }
                                      
                                      return example ? (
                                        <div>
                                          <h4 className="text-sm font-medium text-gray-700 mb-2">💡 具体例</h4>
                                          <ul className="list-disc pl-4 text-sm text-gray-500 space-y-1">
                                            {example.split(',').map((item: string, index: number) => (
                                              <li key={index}>{item.trim()}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      ) : null;
                                    })()}
                                    
                                    {/* 関連タグ */}
                                    {(item['主要タグ'] || item['補助タグ']) && (
                                      <div>
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">🏷️ 関連タグ</h4>
                                        <div className="flex flex-wrap gap-1">
                                          {/* 主要タグ */}
                                          {item['主要タグ'] && item['主要タグ'].split(',').map((tag: string, index: number) => (
                                            <span key={`main-${index}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sand text-gray-600">
                                              #{tag.trim()} : {getTagName(tag.trim())}
                                            </span>
                                          ))}
                                          {/* 補助タグ */}
                                          {item['補助タグ'] && item['補助タグ'].split(',').map((tag: string, index: number) => (
                                            <span key={`sub-${index}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sand text-gray-600">
                                              #{tag.trim()} : {getTagName(tag.trim())}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : viewMode === 'category' ? (
            <>
              {/* カテゴリタブ */}
              {renderCategoryTabs()}

        {/* フィルタされた困りごとカード */}
              <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
                <h3 className="text-base font-medium text-gray-700 mb-4">
                  {selectedCategory}でこんな困りごとはありませんか？（{selected.filter(item => currentCategoryDifficulties.some((d: any) => d['困りごと内容'] === item)).length}/{currentCategoryDifficulties.length}）
                </h3>
                <div className="text-sm text-gray-500 mb-2 text-right">
                  候補の困りごと: {currentCategoryDifficulties.length}件
                </div>
                <div className="grid grid-cols-1 gap-4 content-start">
                  {currentCategoryDifficulties.length === 0 && (
                    <div className="text-gray-400">このカテゴリには該当する困りごとは見つかりませんでした。</div>
                  )}
                  {currentCategoryDifficulties.slice(0, getVisibleCount(selectedCategory, currentCategoryDifficulties.length)).map((item: any) => {
              const isSelected = selected.includes(item['困りごと内容']);
              const isDisabled = !isSelected && selected.length >= maxSelectable;
                    
                    // ドメインに応じた具体例を選択
                    let specificExample = '';
                    if (domain?.name === '企業') {
                      specificExample = item['企業具体例'];
                    } else if (domain?.name === '教育機関') {
                      specificExample = item['教育機関具体例'];
                    } else if (domain?.name === '支援機関') {
                      specificExample = item['支援機関具体例'];
                    }

                    // 具体例をカンマで分割してリスト化
                    const exampleList = specificExample.split(',').map((example, index) => (
                      <li key={index} className="text-sm text-gray-500 mb-1">
                        {example.trim()}
                      </li>
                    ));

                    // タグ情報を取得
                    const mainTags = item['主要タグ'] ? item['主要タグ'].split(',').map((tag: string) => tag.trim()) : [];
                    const subTags = item['補助タグ'] ? item['補助タグ'].split(',').map((tag: string) => tag.trim()) : [];

              const isExpanded = expandedCards.has(item['困りごと内容']);

              return (
                <div
                  key={item['困りごと内容']}
                  className={`rounded-lg border-2 transition-all duration-300 ${
                    isSelected
                      ? 'border-teal-400 bg-gradient-to-br from-teal-50 to-teal-100 shadow-lg'
                      : 'border-gray-200 bg-gradient-to-br from-gray-50 to-white hover:border-teal-300 hover:bg-gradient-to-br hover:from-teal-50 hover:to-white'
                  } ${isDisabled ? 'opacity-40' : ''}`}
                  style={{
                    animation: deselectedCard === item['困りごと内容'] ? 'deselectBounce 0.3s ease-in-out' : undefined
                  }}
                >
                  <div className="p-3">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={(e) => handleSelect(item['困りごと内容'], e)}
                        disabled={isDisabled}
                        className={`flex-1 text-left transition ${
                          isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{item['困りごと内容']}</span>
                          {isSelected && (
                            <div className="ml-auto">
                              <div className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs">✓</span>
                  </div>
                            </div>
                          )}
                        </div>
                </button>
                      
                      <button
                        onClick={() => toggleAccordion(item['困りごと内容'])}
                        className="ml-2 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md transition-colors"
                      >
                        {isExpanded ? '▲閉じる' : '▼詳細'}
                      </button>
                    </div>
                  </div>

                  {/* アコーディオン内容 */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3 bg-gradient-to-b from-transparent to-gray-50/30">
                      <div className="space-y-3">
                        {/* 具体例 */}
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">💡 具体例</h4>
                          <ul className="list-disc pl-4 text-sm text-gray-500 space-y-1">
                            {exampleList}
                          </ul>
                        </div>

                        {/* タグ表示 */}
                        {(mainTags.length > 0 || subTags.length > 0) && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">🏷️ 関連タグ</h4>
                            <div className="flex flex-wrap gap-1">
                              {/* 主要タグ */}
                              {mainTags.map((tag: string, index: number) => (
                                <span key={`main-${index}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sand text-gray-600">
                                  #{tag} : {getTagName(tag)}
                                </span>
                              ))}
                              {/* 補助タグ */}
                              {subTags.map((tag: string, index: number) => (
                                <span key={`sub-${index}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sand text-gray-600">
                                  #{tag} : {getTagName(tag)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* さらに表示ボタン */}
          {currentCategoryDifficulties.length > getVisibleCount(selectedCategory, currentCategoryDifficulties.length) && (
            <div className="mt-2">
              {/* 区切り線とサブ見出し */}
              <div className="flex items-center my-4">
                <div className="flex-1 h-px bg-gray-300"></div>
                <div className="px-4 text-sm text-gray-500 font-medium">— 他の困りごと —</div>
                <div className="flex-1 h-px bg-gray-300"></div>
              </div>
              
              <div className="text-center">
              <button
                  onClick={() => showMore(selectedCategory, currentCategoryDifficulties.length)}
                  className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm font-medium"
              >
                  残りのリストを表示
              </button>
              </div>
            </div>
          )}
        </div>

        {/* カテゴリ検索の下部余白 */}
        <div className="h-24"></div>

            </>
          ) : null}


          {/* グラフ表示 */}
          {viewMode === 'graph' && (
            <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
              <h3 className="text-lg font-medium text-gray-700 mb-4 text-center">
                選択した困りごとの関連性
              </h3>
              <DifficultyGraphView 
                selectedDifficulties={selected}
                domain={domain}
                viewModel={viewModel}
              />
            </div>
          )}
        </div>

        {/* フッター */}
        <StepFooter
          showBackButton={true}
          onBack={onBack}
          onNext={handleNext}
          nextButtonText="🎮 次のステップへ"
          nextButtonDisabled={selected.length === 0}
          isMobile={true}
        />
      </div>
    );
  }

  // PC版UI
  return (
    <div className="max-w-none mx-auto p-6 space-y-8">
      {/* CSSアニメーション */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes flyToDeck {
            0% {
              transform: translate(0, 0) scale(1) rotate(0deg);
              opacity: 1;
            }
            50% {
              transform: translate(calc(100vw - 300px), calc(100vh - 300px)) scale(0.8) rotate(180deg);
              opacity: 0.8;
            }
            100% {
              transform: translate(calc(100vw - 200px), calc(100vh - 200px)) scale(0.3) rotate(360deg);
              opacity: 0;
            }
          }
          @keyframes deselectBounce {
            0% {
              transform: scale(1) rotate(0deg);
              background: linear-gradient(to bottom right, rgb(239 246 255), rgb(219 234 254));
              border-color: rgb(129 140 248);
            }
            25% {
              transform: scale(0.95) rotate(-2deg);
              background: linear-gradient(to bottom right, rgb(254 242 242), rgb(252 231 243));
              border-color: rgb(239 68 68);
            }
            50% {
              transform: scale(1.05) rotate(2deg);
              background: linear-gradient(to bottom right, rgb(254 242 242), rgb(252 231 243));
              border-color: rgb(239 68 68);
            }
            75% {
              transform: scale(0.98) rotate(-1deg);
              background: linear-gradient(to bottom right, rgb(254 242 242), rgb(252 231 243));
              border-color: rgb(239 68 68);
            }
            100% {
              transform: scale(1) rotate(0deg);
              background: linear-gradient(to bottom right, rgb(255 255 255), rgb(255 255 255));
              border-color: rgb(209 213 219);
            }
          }
          @keyframes deckRemove {
            0% {
              transform: scale(1) translateY(0px);
              opacity: 1;
            }
            20% {
              transform: scale(1.1) translateY(-5px);
              opacity: 0.9;
            }
            40% {
              transform: scale(0.9) translateY(2px);
              opacity: 0.8;
            }
            60% {
              transform: scale(1.05) translateY(-2px);
              opacity: 0.9;
            }
            80% {
              transform: scale(0.95) translateY(1px);
              opacity: 0.95;
            }
            100% {
              transform: scale(1) translateY(0px);
              opacity: 1;
            }
          }
          @keyframes deckAdd {
            0% {
              transform: scale(1) translateY(0px);
              opacity: 1;
            }
            25% {
              transform: scale(1.2) translateY(-10px);
              opacity: 0.8;
            }
            50% {
              transform: scale(0.8) translateY(5px);
              opacity: 0.9;
            }
            75% {
              transform: scale(1.1) translateY(-3px);
              opacity: 0.95;
            }
            100% {
              transform: scale(1) translateY(0px);
              opacity: 1;
            }
          }
          @keyframes cardSlideIn {
            0% {
              transform: translate(-80px, -40px) rotate(-15deg) scale(0.8);
              opacity: 0;
            }
            50% {
              transform: translate(-15px, -15px) rotate(-5deg) scale(0.9);
              opacity: 0.8;
            }
            100% {
              transform: translate(0, 0) rotate(0deg) scale(1);
              opacity: 0;
            }
          }
          @keyframes cardSlideOut {
            0% {
              transform: translate(0, 0) rotate(0deg) scale(1);
              opacity: 0.8;
            }
            50% {
              transform: translate(-25px, -25px) rotate(-10deg) scale(0.9);
              opacity: 0.6;
            }
            100% {
              transform: translate(-80px, -40px) rotate(-20deg) scale(0.7);
              opacity: 0;
            }
          }
        `
      }} />
      {/* 選択済み困りごとモーダル */}
      {showSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            {/* モーダルヘッダー */}
            <div className="bg-teal-500 text-white px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                📚 集めた困りごとカード
                <span className={selected.length >= 11 ? 'text-red-500' : 'text-white'}>
                  （{selected.length}枚）
                </span>
              </h3>
                <button
                onClick={() => setShowSelectionModal(false)}
                className="text-white bg-teal-600 hover:bg-teal-700 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full"
                >
                ✕
                </button>
            </div>
            
            {/* モーダルコンテンツ */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {selected.length === 0 ? (
                <p className="text-gray-500 text-center">選択された困りごとはありません</p>
              ) : (
                <div className="space-y-3">
                  {/* 全て削除ボタン */}
                  <div className="mb-4">
                    <button
                      onClick={() => {
                        // 全ての選択を解除
                        selected.forEach(item => handleSelect(item));
                        // モーダルを閉じる
                        setTimeout(() => setShowSelectionModal(false), 100);
                      }}
                      className="w-full px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium"
                    >
                      全て削除 ({selected.length}件)
                    </button>
                  </div>
                  
                  {/* 個別削除リスト */}
                  <div className="space-y-3">
                    {selected.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-sand rounded-lg">
                      <span className="text-gray-700 flex-1">{item}</span>
                <button
                          onClick={() => {
                            handleSelect(item); // 選択解除（モーダルは閉じない）
                          }}
                          className="ml-3 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                        >
                          削除
                </button>
                    </div>
                    ))}
                  </div>
            </div>
          )}
        </div>

            {/* モーダルフッター */}
            <div className="px-6 py-4 border-t bg-sand">
              <button
                onClick={() => setShowSelectionModal(false)}
                className="w-full px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
              >
                閉じる
              </button>
            </div>
          </div>
            </div>
          )}
      

      
      {/* 説明文 */}
      <div className="bg-light-sand border border-teal-500 rounded-lg p-6 mb-8">
        <p className="text-gray-700 text-lg leading-relaxed">
          <span className="font-semibold">🗺️ 探索：あなたの困りごとを探そう</span><br />
          キーワード または カテゴリ で、あなたの抱える困りごとカードを見つけましょう。
        </p>
        </div>

      {/* ビューモード切り替えタブ */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="mb-4">
          <div className="flex space-x-1 bg-sand p-1 rounded-lg">
            <button
              onClick={() => setViewMode('keyword')}
              className={`flex-1 py-2 px-8 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'keyword'
                  ? 'bg-white text-teal-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              🔍 キーワード
            </button>
            <button
              onClick={() => setViewMode('category')}
              className={`flex-1 py-2 px-8 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'category'
                  ? 'bg-white text-teal-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              📂 カテゴリ
            </button>
          </div>
        </div>
        
        {/* グラフ表示ボタン */}
        <div className="flex justify-center">
          <div className={`w-full p-1 rounded-lg ${viewMode === 'graph' ? 'bg-sand' : ''}`}>
            <button
              onClick={() => setViewMode('graph')}
              className={`w-full py-3 px-6 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'graph'
                  ? 'bg-white text-teal-600 shadow-sm'
                  : 'bg-sand text-gray-500 hover:text-gray-700'
              }`}
            >
              🔗 グラフ表示
            </button>
          </div>
        </div>
          </div>

      <div className="space-y-6">
        {viewMode === 'keyword' ? (
          <>
            {/* キーワード検索セクション */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h3 className="text-xl font-medium text-gray-700 mb-4">
                🔍 困りごとをキーワードで探す
              </h3>
              <div className="flex gap-3 mb-4">
            <input
              type="text"
                  value={customSearchTerm}
                  onChange={(e) => setCustomSearchTerm(e.target.value)}
                  placeholder="例：タスク、遅刻、休憩etc…"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-lg"
            />
            <button
                  onClick={() => {
                    setShowCustomSearch(!showCustomSearch);
                    // キーワード検索ログ
                    if (!showCustomSearch && customSearchTerm.trim()) {
                      logSearch(customSearchTerm, customSearchResults.length);
                    }
                  }}
                  className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                    showCustomSearch
                      ? 'bg-teal-500 text-white shadow-lg'
                      : 'bg-teal-500 text-white hover:bg-teal-600'
                  }`}
                >
                  検索
            </button>
          </div>

              {/* 検索結果表示 */}
              {showCustomSearch && customSearchTerm.trim() && (
                <div className="space-y-4">
                  <div className="text-sm text-gray-600 mb-4">
                    「{customSearchTerm}」の検索結果: {customSearchResults.length}件
                  </div>
                  {customSearchResults.length === 0 ? (
                    <div className="text-gray-400 text-center py-8">
                      該当する困りごとは見つかりませんでした。
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4">
                      {customSearchResults.map((item: any) => {
                        const isSelected = selected.includes(item['困りごと内容']);
                        const isDisabled = !isSelected && selected.length >= maxSelectable;
                        
                        return (
                          <div
                            key={item['困りごと内容']}
                            className={`rounded-lg border-2 transition-all duration-300 ${
                              isSelected
                                ? 'border-teal-400 bg-gradient-to-br from-teal-50 to-teal-100 shadow-lg'
                                : 'border-gray-200 bg-gradient-to-br from-gray-50 to-white hover:border-teal-300 hover:bg-gradient-to-br hover:from-teal-50 hover:to-white'
                            } ${isDisabled ? 'opacity-40' : ''}`}
                          >
                            <div className="p-4">
                              <div className="flex items-center justify-between">
                    <button
                                  onClick={(e) => handleSelect(item['困りごと内容'], e)}
                                  disabled={isDisabled}
                                  className={`flex-1 text-left transition ${
                                    isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900">
                                      {highlightSearchTerm(item['困りごと内容'], customSearchTerm)}
                                    </span>
                                    {isSelected && (
                                      <div className="ml-auto">
                                        <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                                          <span className="text-white text-sm">✓</span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                    </button>
                                  <button
                                    onClick={() => toggleAccordion(item['困りごと内容'])}
                                    className="ml-2 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md transition-colors"
                                  >
                                    {expandedCards.has(item['困りごと内容']) ? '▲閉じる' : '▼詳細'}
                                  </button>
                              </div>
                            </div>
                            
                            {/* アコーディオン内容 */}
                            {expandedCards.has(item['困りごと内容']) && (
                              <div className="px-4 pb-4 border-t border-gray-100 pt-3 bg-gradient-to-b from-transparent to-gray-50/30">
                                <div className="space-y-3">
                                  {/* 困りごと説明 */}
                                  {item['困りごと説明'] && (
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2">📝 説明</h4>
                                      <p className="text-sm text-gray-500 leading-relaxed">
                                        {item['困りごと説明']}
                                      </p>
                                    </div>
                                  )}
                                  
                                  {/* 具体例 */}
                                  {(() => {
                                    let example = '';
                                    if (domain?.name === '企業' && item['企業具体例']) {
                                      example = item['企業具体例'];
                                    } else if (domain?.name === '教育機関' && item['教育機関具体例']) {
                                      example = item['教育機関具体例'];
                                    } else if (domain?.name === '支援機関' && item['支援機関具体例']) {
                                      example = item['支援機関具体例'];
                                    }
                                    
                                    return example ? (
                                      <div>
                                        <h4 className="text-sm font-medium text-gray-700 mb-2">💡 具体例</h4>
                                        <ul className="list-disc pl-4 text-sm text-gray-500 space-y-1">
                                          {example.split(',').map((item: string, index: number) => (
                                            <li key={index}>{item.trim()}</li>
                ))}
              </ul>
                                      </div>
                                    ) : null;
                                  })()}
                                  
                                  {/* 関連タグ */}
                                  {(item['主要タグ'] || item['補助タグ']) && (
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2">🏷️ 関連タグ</h4>
                                      <div className="flex flex-wrap gap-1">
                                        {/* 主要タグ */}
                                        {item['主要タグ'] && item['主要タグ'].split(',').map((tag: string, index: number) => (
                                          <span key={`main-${index}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sand text-gray-600">
                                            #{tag.trim()} : {getTagName(tag.trim())}
                                          </span>
                                        ))}
                                        {/* 補助タグ */}
                                        {item['補助タグ'] && item['補助タグ'].split(',').map((tag: string, index: number) => (
                                          <span key={`sub-${index}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sand text-gray-600">
                                            #{tag.trim()} : {getTagName(tag.trim())}
                                          </span>
                                        ))}
                                      </div>
            </div>
          )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : viewMode === 'category' ? (
          <>
            {/* カテゴリタブと選択数表示 */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                {renderCategoryTabs()}
              </div>
              <div className="ml-4">
                <div 
                  className={`inline-flex items-center justify-center px-6 py-3 rounded-full shadow-lg transition-all duration-300 cursor-pointer hover:opacity-80 ${
                    isMaxReached
                      ? 'scale-125 bg-red-500 shadow-2xl animate-pulse'
                      : isCountAnimating
                        ? 'scale-110 bg-yellow-500 shadow-xl'
                        : isCountDecreasing
                          ? 'scale-90 bg-blue-400 shadow-sm animate-bounce'
                          : 'bg-teal-500 scale-100'
                  }`}
                  onClick={() => setShowSelectionModal(true)}
                >
                  <span className="text-lg font-bold text-white">
                    {selected.length}枚
                  </span>
                </div>
              </div>
        </div>

            {/* フィルタされた困りごとカード */}
            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
              <h3 className="text-xl font-medium text-gray-700 mb-4">
                {selectedCategory}でこんな困りごとはありませんか？（{selected.filter(item => currentCategoryDifficulties.some((d: any) => d['困りごと内容'] === item)).length}/{currentCategoryDifficulties.length}）
              </h3>
              <div className="text-sm text-gray-500 mb-4 text-right">
                候補の困りごと: {currentCategoryDifficulties.length}件
              </div>
              <div className="grid grid-cols-1 gap-4 content-start">
                {currentCategoryDifficulties.length === 0 && (
                  <div className="text-gray-400 col-span-2">このカテゴリには該当する困りごとは見つかりませんでした。</div>
                )}
                {currentCategoryDifficulties.slice(0, getVisibleCount(selectedCategory, currentCategoryDifficulties.length)).map((item: any) => {
                  const isSelected = selected.includes(item['困りごと内容']);
                  const isDisabled = !isSelected && selected.length >= maxSelectable;
                  
                  // ドメインに応じた具体例を選択
                  let specificExample = '';
                  if (domain?.name === '企業') {
                    specificExample = item['企業具体例'];
                  } else if (domain?.name === '教育機関') {
                    specificExample = item['教育機関具体例'];
                  } else if (domain?.name === '支援機関') {
                    specificExample = item['支援機関具体例'];
                  }

                  // 具体例をカンマで分割してリスト化
                  const exampleList = specificExample.split(',').map((example, index) => (
                    <li key={index} className="text-sm text-gray-500 mb-1">
                      {example.trim()}
                  </li>
                  ));

                  // タグ情報を取得
                  const mainTags = item['主要タグ'] ? item['主要タグ'].split(',').map((tag: string) => tag.trim()) : [];
                  const subTags = item['補助タグ'] ? item['補助タグ'].split(',').map((tag: string) => tag.trim()) : [];

                  const isExpanded = expandedCards.has(item['困りごと内容']);

                  return (
                    <div
                      key={item['困りごと内容']}
                      className={`rounded-lg border-2 transition-all duration-300 ${
                        isSelected
                          ? 'border-teal-400 bg-gradient-to-br from-teal-50 to-teal-100 shadow-lg'
                          : 'border-gray-200 bg-gradient-to-br from-gray-50 to-white hover:border-teal-300 hover:bg-gradient-to-br hover:from-teal-50 hover:to-white'
                      } ${isDisabled ? 'opacity-40' : ''}`}
                      style={{
                        animation: deselectedCard === item['困りごと内容'] ? 'deselectBounce 0.3s ease-in-out' : undefined
                      }}
                    >
                      {/* カードのヘッダー部分（選択可能） */}
                      <div className="p-3">
                        <div className="flex items-center justify-between">
          <button
                            onClick={(e) => handleSelect(item['困りごと内容'], e)}
                            disabled={isDisabled}
                            className={`flex-1 text-left transition ${
                              isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 text-sm">{item['困りごと内容']}</span>
                              {isSelected && (
                                <div className="ml-auto">
                                  <div className="w-5 h-5 bg-teal rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs">✓</span>
                                  </div>
            </div>
          )}
        </div>
          </button>

          <button
                            onClick={() => toggleAccordion(item['困りごと内容'])}
                            className="ml-2 px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-md transition-colors"
          >
                            {isExpanded ? '▲閉じる' : '▼詳細'}
          </button>
        </div>
      </div>

                      {/* アコーディオン内容 */}
                      {isExpanded && (
                        <div className="px-3 pb-3 border-t border-gray-100 pt-3 bg-gradient-to-b from-transparent to-gray-50/30">
                          <div className="space-y-3">
                            {/* 具体例 */}
                            <div>
                              <h4 className="text-sm font-medium text-gray-700 mb-2">💡 具体例</h4>
                              <ul className="list-disc pl-4 text-sm text-gray-500 space-y-1">
                                {exampleList}
              </ul>
                            </div>

                            {/* タグ表示 */}
                            {(mainTags.length > 0 || subTags.length > 0) && (
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 mb-2">🏷️ 関連タグ</h4>
                                <div className="flex flex-wrap gap-1">
                                  {/* 主要タグ */}
                                  {mainTags.map((tag: string, index: number) => (
                                    <span key={`main-${index}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sand text-gray-600">
                                      #{tag} : {getTagName(tag)}
                                    </span>
                                  ))}
                                  {/* 補助タグ */}
                                  {subTags.map((tag: string, index: number) => (
                                    <span key={`sub-${index}`} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-sand text-gray-600">
                                      #{tag} : {getTagName(tag)}
                                    </span>
                                  ))}
                                </div>
            </div>
          )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
        </div>

              {/* さらに表示ボタン */}
              {currentCategoryDifficulties.length > getVisibleCount(selectedCategory, currentCategoryDifficulties.length) && (
                <div className="mt-2">
                  {/* 区切り線とサブ見出し */}
                  <div className="flex items-center my-4">
                    <div className="flex-1 h-px bg-gray-300"></div>
                    <div className="px-4 text-sm text-gray-500 font-medium">— 他の困りごと —</div>
                    <div className="flex-1 h-px bg-gray-300"></div>
                  </div>
                  
                  <div className="text-center">
          <button
                      onClick={() => showMore(selectedCategory, currentCategoryDifficulties.length)}
                      className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-sm font-medium"
          >
                      残りのリストを表示
          </button>
        </div>
      </div>
              )}
            </div>


          </>
        ) : null}


        {/* グラフ表示 */}
        {viewMode === 'graph' && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
            <h3 className="text-xl font-medium text-gray-700 mb-4 text-center">
              選択した困りごとの関連性
            </h3>
            <DifficultyGraphView 
              selectedDifficulties={selected}
              domain={domain}
              viewModel={viewModel}
            />
          </div>
        )}
        </div>

        {/* 下部余白 */}
        <div className="h-24"></div>

      {/* フッター */}
      <StepFooter
        showBackButton={true}
        onBack={onBack}
        onNext={handleNext}
        nextButtonText="🎮 次のステップへ"
        nextButtonDisabled={selected.length === 0}
        isMobile={false}
      />
      
    </div>
  );
};

export default DifficultyThinking; 